import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { monthParam, searchUpdate } from "@/lib/search";
import { api } from "@convex/_generated/api";
import {
  LuCalendarPlus,
  LuChevronLeft,
  LuChevronRight,
} from "react-icons/lu";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Field,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { LoadingCard } from "../components/LoadingCard";
import { PageHeader } from "../components/PageHeader";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";
import { useEffectivePersonId } from "../impersonation";
import { useViewerAccess } from "@/lib/access";
import { eventKindLabel, monthLabel, shiftMonth, monthRange, todayInVienna } from "./eventsShared";

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Events" },
];

/**
 * The calendar.
 *
 * A month at a time rather than "the next N days", because the two audiences
 * want different windows out of the same control: somebody looking for a
 * workshop wants what is coming, and an admin planning a term wants to page
 * through it. A month stepper serves both and keeps the query bounded, which
 * "everything" would not.
 */
export function validateEventsSearch(search: Record<string, unknown>): { month?: string } {
  return { month: monthParam(search.month) };
}

export function EventsPage() {
  const effectivePersonId = useEffectivePersonId();
  // ?month=2026-11; this month when absent, and never written as itself.
  const thisMonth = todayInVienna().slice(0, 7);
  const month = useSearch({ from: "/_authed/events" }).month ?? thisMonth;
  const navigateHere = useNavigate({ from: "/events" });
  const setMonth = (next: string) =>
    void navigateHere({ search: { month: next === thisMonth ? undefined : next }, ...searchUpdate });
  const [creating, setCreating] = useState(false);

  const { fromDay, toDay } = useMemo(() => monthRange(month), [month]);

  const events = useQuery(api.events.listEvents, { fromDay, toDay, includeCancelled: true });
  const mine = useQuery(api.participations.listForPerson, { personId: null, effectivePersonId });

  const myStates = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of mine ?? []) {
      if (row.eventId !== null) {
        map.set(row.eventId, row.state);
      }
    }
    return map;
  }, [mine]);

  const isAdmin = useViewerAccess()?.isActivityAdmin ?? false;

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        eyebrow="Events"
        title="Events"
        description="Seminars, workshops and institute days. Sign up for what is open, and see what you are down for."
        actions={isAdmin ? (
          <Button size="sm" colorPalette="blue" onClick={() => setCreating(true)}>
            <LuCalendarPlus />New event
          </Button>
        ) : undefined}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={6}>
        <Stack gap={5}>
          <HStack justify="space-between">
            <HStack gap={1}>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Previous month"
                onClick={() => setMonth(shiftMonth(month, -1))}
              >
                <LuChevronLeft />
              </Button>
              <Heading size="md" minW="14rem" textAlign="center">{monthLabel(month)}</Heading>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Next month"
                onClick={() => setMonth(shiftMonth(month, 1))}
              >
                <LuChevronRight />
              </Button>
            </HStack>
            {month === thisMonth ? null : (
              <Button size="xs" variant="plain" onClick={() => setMonth(thisMonth)}>
                Back to this month
              </Button>
            )}
          </HStack>

          {events === undefined ? (
            <LoadingCard label="Loading events…" />
          ) : events.length === 0 ? (
            <Card.Root borderWidth="1px" borderColor="border">
              <Card.Body p={6}>
                <Text fontSize="sm" color="fg.muted">
                  Nothing in {monthLabel(month)}.
                </Text>
              </Card.Body>
            </Card.Root>
          ) : (
            <Stack gap={3}>
              {events.map((event) => (
                <EventRow key={event._id} event={event} myState={myStates.get(event._id) ?? null} />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      {isAdmin ? <CreateEventDialog open={creating} onClose={() => setCreating(false)} /> : null}
    </Box>
  );
}

type EventSummary = NonNullable<ReturnType<typeof useQuery<typeof api.events.listEvents>>>[number];

function EventRow({ event, myState }: { event: EventSummary; myState: string | null }) {
  const cancelled = event.status === "cancelled";

  return (
    <Card.Root
      borderWidth="1px"
      borderColor="border"
      opacity={cancelled ? 0.6 : 1}
      _hover={{ borderColor: cancelled ? "border" : "blue.400" }}
    >
      <Card.Body p={4}>
        <HStack justify="space-between" align="start" gap={4} wrap="wrap">
          <Stack gap={1} minW="16rem" flex="1">
            <HStack gap={2} wrap="wrap">
              <Link to="/events/$eventId" params={{ eventId: event._id }}>
                <Text fontWeight="semibold" textDecoration={cancelled ? "line-through" : undefined}>
                  {event.title}
                </Text>
              </Link>
              <Badge size="sm" variant="subtle">{eventKindLabel(event.kind)}</Badge>
              {event.seriesName === null ? null : (
                <Badge size="sm" variant="outline" colorPalette="purple">{event.seriesName}</Badge>
              )}
              {cancelled ? <Badge size="sm" colorPalette="red">Cancelled</Badge> : null}
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              {event.startDay}
              {event.endDay === null || event.endDay === event.startDay ? "" : ` – ${event.endDay}`}
              {event.startTime === null ? "" : ` · ${event.startTime}`}
              {event.location === "" ? "" : ` · ${event.location}`}
            </Text>
          </Stack>

          <HStack gap={2}>
            {myState === null || cancelled ? null : (
              <Badge
                size="sm"
                colorPalette={myState === "waitlisted" ? "orange" : myState === "cancelled" ? "gray" : "green"}
              >
                {myState === "waitlisted"
                  ? "On the waitlist"
                  : myState === "cancelled"
                    ? "Withdrawn"
                    : myState === "confirmed"
                      ? "Attended"
                      : "You are going"}
              </Badge>
            )}
            {!cancelled && event.signUpOpen ? (
              <Badge size="sm" variant="outline" colorPalette="blue">Sign-up open</Badge>
            ) : null}
          </HStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

function CreateEventDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const createEvent = useMutation(api.events.createEvent);
  const series = useQuery(api.events.listSeries, open ? {} : "skip");

  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  function set<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function save() {
    setSaving(true);
    try {
      const eventId = await createEvent({
        seriesId: draft.seriesId === "" ? null : (draft.seriesId as never),
        title: draft.title,
        description: draft.description,
        kind: draft.kind as never,
        startDay: draft.startDay,
        endDay: draft.endDay === "" ? null : draft.endDay,
        startTime: draft.startTime === "" ? null : draft.startTime,
        endTime: draft.endTime === "" ? null : draft.endTime,
        location: draft.location,
        capacity: draft.capacity === "" ? null : Number(draft.capacity),
        signUpOpen: draft.signUpOpen,
        waitlistEnabled: draft.waitlistEnabled,
      });
      toastSuccess("Event created", draft.title);
      setDraft(emptyDraft());
      onClose();
      void navigate({ to: "/events/$eventId", params: { eventId } });
    } catch (error) {
      const { message, detail } = readableError(error, "The event could not be created");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>New event</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Field.Root required>
                  <Field.Label>Title</Field.Label>
                  <Input value={draft.title} onChange={(e) => set("title", e.target.value)} />
                </Field.Root>

                <Field.Root>
                  <Field.Label>Description</Field.Label>
                  <Textarea
                    rows={2}
                    value={draft.description}
                    onChange={(e) => set("description", e.target.value)}
                  />
                </Field.Root>

                <HStack gap={3} align="end">
                  <Field.Root>
                    <Field.Label>Kind</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={draft.kind} onChange={(e) => set("kind", e.target.value)}>
                        <option value="seminar">Seminar</option>
                        <option value="workshop">Workshop</option>
                        <option value="instituteDay">Institute day</option>
                        <option value="other">Other</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Series</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        value={draft.seriesId}
                        onChange={(e) => set("seriesId", e.target.value)}
                      >
                        <option value="">None — a one-off</option>
                        {(series ?? []).map((row) => (
                          <option key={row._id} value={row._id}>{row.name}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </HStack>

                <HStack gap={3}>
                  <Field.Root required>
                    <Field.Label>Starts</Field.Label>
                    <Input type="date" value={draft.startDay} onChange={(e) => set("startDay", e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Ends</Field.Label>
                    <Input type="date" value={draft.endDay} onChange={(e) => set("endDay", e.target.value)} />
                    <Field.HelperText>Only for something spanning days.</Field.HelperText>
                  </Field.Root>
                </HStack>

                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>From</Field.Label>
                    <Input type="time" value={draft.startTime} onChange={(e) => set("startTime", e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>To</Field.Label>
                    <Input type="time" value={draft.endTime} onChange={(e) => set("endTime", e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Places</Field.Label>
                    <Input
                      type="number"
                      min={1}
                      value={draft.capacity}
                      onChange={(e) => set("capacity", e.target.value)}
                    />
                    <Field.HelperText>Blank for no limit.</Field.HelperText>
                  </Field.Root>
                </HStack>

                <Field.Root>
                  <Field.Label>Location</Field.Label>
                  <Input value={draft.location} onChange={(e) => set("location", e.target.value)} />
                </Field.Root>

                <HStack gap={6}>
                  <Field.Root orientation="horizontal">
                    <Field.Label>Sign-up open</Field.Label>
                    <input
                      type="checkbox"
                      checked={draft.signUpOpen}
                      onChange={(e) => set("signUpOpen", e.target.checked)}
                    />
                  </Field.Root>
                  <Field.Root orientation="horizontal">
                    <Field.Label>Keep a waitlist</Field.Label>
                    <input
                      type="checkbox"
                      checked={draft.waitlistEnabled}
                      onChange={(e) => set("waitlistEnabled", e.target.checked)}
                    />
                  </Field.Root>
                </HStack>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="blue"
                loading={saving}
                disabled={draft.title.trim() === "" || draft.startDay === ""}
                onClick={() => void save()}
              >
                Create
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function emptyDraft() {
  return {
    title: "",
    description: "",
    kind: "workshop",
    seriesId: "",
    startDay: "",
    endDay: "",
    startTime: "",
    endTime: "",
    location: "",
    capacity: "",
    signUpOpen: true,
    waitlistEnabled: true,
  };
}
